import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { mediaUrl, formatPrice } from "@/lib/api";
import { PageHead, Btn } from "@/admin/ui";
import { toast } from "sonner";
import { GripVertical, Loader2, Sparkles } from "lucide-react";

const STATUS_COLORS = { live: "bg-sage", draft: "bg-espresso/30", sold_out: "bg-wine", coming_soon: "bg-gold" };
const totalStock = (p) => (p.colors || []).reduce((n, c) => n + (c.sizes || []).reduce((m, s) => m + (Number(s.stock) || 0), 0), 0);
const notShoppable = (p) => p.status === "live" && (p.price == null || totalStock(p) <= 0);
const shopIssue = (p) => { const noP = p.price == null, noS = totalStock(p) <= 0; return noP && noS ? "No price · No stock" : noP ? "No price" : "No stock"; };

const ProductsAdmin = () => {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [colFilter, setColFilter] = useState("");
  const [waitCounts, setWaitCounts] = useState({});
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/collections").then((r) => setCollections(r.data)).catch(() => {}); api.get("/admin/waitlist/counts").then((r) => setWaitCounts(r.data)).catch(() => {}); }, []);

  const visible = colFilter ? products.filter((p) => p.collection_slug === colFilter) : products;

  const create = async () => {
    try {
      const { data } = await api.post("/admin/products", {
        name: "Untitled Piece", status: "draft", department: "women", category: "Sets",
        price: null, colors: [], badges: [], related_product_ids: [], seo: {},
      });
      nav(`/admin/products/${data.id}`);
    } catch { toast.error("Could not create product"); }
  };
  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load();
  };

  const dragIdx = useRef(null);
  const onDrop = async (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const arr = [...visible]; const [m] = arr.splice(from, 1); arr.splice(i, 0, m); dragIdx.current = null;
    try { await api.post("/admin/products/reorder", { ids: arr.map((x) => x.id) }); toast.success("Order saved"); load(); }
    catch { toast.error("Reorder failed"); }
  };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const needsCopy = (p) => !p.short_description || !p.long_description;
  const dup = async (id, e) => { e.stopPropagation(); await api.post(`/admin/products/${id}/duplicate`); toast.success("Duplicated"); load(); };
  const bulkFill = async () => {
    if (!selected.length) { toast("Select products first"); return; }
    setBusy(true);
    try { const { data } = await api.post("/admin/products/bulk-fill", { ids: selected }); toast.success(`Filled ${data.filled} product(s) — review & save`); setSelected([]); load(); }
    catch { toast.error("Bulk fill failed"); }
    setBusy(false);
  };

  return (
    <div>
      <PageHead title="Products" sub={`${visible.length} products`}>
        <select value={colFilter} onChange={(e) => { setColFilter(e.target.value); setSelected([]); }} data-testid="products-collection-filter"
          className="bg-ivory border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso">
          <option value="">All collections</option>
          {collections.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
        </select>
        {selected.length > 0 && (
          <Btn variant="ghost" onClick={bulkFill} disabled={busy} testid="products-bulk-fill">
            {busy ? <Loader2 size={13} className="animate-spin inline" /> : <Sparkles size={13} className="inline" />} Fill {selected.length} with AI
          </Btn>
        )}
        <Btn onClick={create} testid="admin-new-product">+ New Product</Btn>
      </PageHead>
      <p className="text-xs text-espresso/40 mb-3">Drag rows by the handle to set the order pieces appear in{colFilter ? " this collection" : ""}. Tick pieces to draft their copy in bulk.</p>
      <div className="bg-cream border border-espresso/10">
        {visible.map((p, idx) => (
          <div key={p.id} draggable onDragStart={() => (dragIdx.current = idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
            data-testid={`admin-product-row-${p.slug}`}
            className="flex items-center gap-4 px-5 py-3 border-b border-espresso/5 hover:bg-sand/40">
            <GripVertical size={16} className="text-espresso/30 cursor-move shrink-0" />
            <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSel(p.id)} data-testid={`product-select-${p.slug}`} className="shrink-0 accent-espresso" />
            <img src={mediaUrl(p.colors?.[0]?.main_image)} alt="" onClick={() => nav(`/admin/products/${p.id}`)} className="w-11 h-14 object-cover bg-sand cursor-pointer" />
            <div className="flex-1 cursor-pointer" onClick={() => nav(`/admin/products/${p.id}`)}>
              <p className="font-serif text-lg leading-tight">{p.name}</p>
              <p className="text-xs text-espresso/50">{p.category} · {p.colors?.length || 0} colours · {formatPrice(p.price)}</p>
            </div>
            {waitCounts[p.slug] > 0 && <span className="text-[0.6rem] tracking-[0.14em] uppercase text-espresso border border-espresso/30 px-2.5 py-1" data-testid={`waitlist-count-${p.slug}`} title="People waiting for this piece">{waitCounts[p.slug]} waiting</span>}
            {notShoppable(p) && <span className="text-[0.6rem] tracking-[0.14em] uppercase text-cream bg-wine px-2.5 py-1" data-testid={`not-shoppable-${p.slug}`} title="Live but shoppers can't buy it yet">{shopIssue(p)}</span>}
            {needsCopy(p) && <span className="text-[0.6rem] tracking-[0.14em] uppercase text-espresso border border-gold bg-gold/20 px-2.5 py-1" data-testid={`needs-copy-${p.slug}`}>Needs copy</span>}
            <span className={`text-[0.6rem] tracking-[0.14em] uppercase text-cream px-2.5 py-1 ${STATUS_COLORS[p.status] || "bg-espresso/30"}`}>{(p.status || "draft").replace("_", " ")}</span>
            <button onClick={(e) => dup(p.id, e)} className="text-espresso/50 text-xs tracking-widest uppercase">Duplicate</button>
            <button onClick={(e) => del(p.id, e)} className="text-wine text-xs tracking-widest uppercase">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductsAdmin;
